import React from 'react';
import { SegmentedControl, SparkIcon, TooltipTrigger } from '@spark';
import { TaskType } from '@/shared/domain/taskTypes';
import { ContactUsTooltipContent, CONTACT_US_TASK_TYPE, useSaasGating } from '@/features/customer-intents';
import LabelsTagInput from '../businessGoals/LabelsTagInput';

export const TASK_TYPE_ITEMS = [
    { id: TaskType.classification, label: 'Classification' },
    { id: TaskType.NER, label: 'Entity Extraction' },
    { id: TaskType.summarization, label: 'Summarization' },
    { id: TaskType.contentGeneration, label: 'Content Generation' },
];

export const CLASSIFICATION_TYPE_ITEMS = [
    {
        id: 'single',
        label: 'No',
        tooltip:
            'The model picks a single label for each example. (A review is Positive or Negative - not both.)',
    },
    {
        id: 'multi',
        label: 'Yes',
        tooltip:
            'The model can pick multiple labels for the same example. (A ticket can be Bug and Urgent at the same time.)',
    },
];

export const HelpIcon = ({ text, content }) => (
    <TooltipTrigger text={text} content={content}>
        <span className="cw-help-icon">
            <SparkIcon slug="circle-help" size="small" />
        </span>
    </TooltipTrigger>
);

const OptionInfoIcon = ({ text }) => (
    <TooltipTrigger text={text}>
        <span
            className="cw-help-icon"
            onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <SparkIcon slug="circle-help" size="small" />
        </span>
    </TooltipTrigger>
);

/**
 * Shared "Task type" block used by the Business Goals step and the Edit Task
 * modal (Figma 3047-4189 / 3088-7897 / 3087-6762): explainer text plus the
 * task-type and classification-type segmented controls. Confidence rejection
 * has no checkbox here — the required fallback-label select (No
 * predictions / a label, plus Best guess on single-label) plays that role in
 * both surfaces. Controlled through `onPatch` partial-form updates.
 *
 * On SaaS only Classification is self-serve (Figma 9035-9521): the other
 * types are disabled with a "Contact us to enable this task type" tooltip
 * whose link calls `onContactUs` (the customers-intents modal). A task that
 * already has a gated type keeps that segment enabled so editing it works.
 */
export const TaskTypeControls = ({
    taskType,
    classificationType,
    onPatch,
    explainerText,
    taskTypeError,
    classificationTypeError,
    onContactUs,
}) => {
    const isClassification = taskType === TaskType.classification;
    const { gated, contactUs } = useSaasGating(onContactUs);
    const taskTypeItems = gated
        ? TASK_TYPE_ITEMS.map((item) =>
              item.id === TaskType.classification || item.id === taskType
                  ? item
                  : {
                        ...item,
                        disabled: true,
                        tooltipContent: (
                            <ContactUsTooltipContent text={CONTACT_US_TASK_TYPE} onContactUs={contactUs} />
                        ),
                    },
          )
        : TASK_TYPE_ITEMS;

    return (
        <div className="cw-type-block">
            {explainerText && <p className="cw-explainer">{explainerText}</p>}

            <div className="cw-type-row">
                <div className="cw-type-row__group cw-type-row__group--task-type">
                    <div className="cw-section-label">
                        Task type<span className="cw-required-asterisk">*</span>
                        <HelpIcon text="Each task type produces one output: Classification = labels, Entity Extraction = spans, Summarization / Content Generation = generated text." />
                    </div>
                    <SegmentedControl
                        size="medium"
                        items={taskTypeItems}
                        activeId={taskType}
                        onChange={(nextType) => onPatch({ taskType: nextType })}
                    />
                    {taskTypeError && <div className="cw-field-error">{taskTypeError}</div>}
                </div>

                {isClassification && (
                    <div className="cw-type-row__group">
                        <div className="cw-section-label">
                            Can one example have more than one label?
                        </div>
                        <SegmentedControl
                            size="medium"
                            items={CLASSIFICATION_TYPE_ITEMS.map((item) => ({
                                id: item.id,
                                label: item.label,
                                endIcon: <OptionInfoIcon text={item.tooltip} />,
                            }))}
                            activeId={classificationType}
                            onChange={(next) => onPatch({ classificationType: next })}
                        />
                        {classificationTypeError && (
                            <div className="cw-field-error">{classificationTypeError}</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * "Entities in this task" (NER) / "Labels in this task" (classification)
 * section of the Business Goals step — required-asterisk label, help
 * tooltip and the tag input. `readOnly` renders the labels as display-only
 * chips (Duplicate Task modal — labels are copied from the source task).
 */
export const TaskLabelsField = ({ taskType, labels, onPatch, error, readOnly = false }) => {
    const isNer = taskType === TaskType.NER;
    const labelsTitle = isNer ? 'Entities in this task' : 'Labels in this task';

    return (
        <div className="cw-labels-section">
            <div className="cw-section-label">
                {labelsTitle}
                {!readOnly && <span className="cw-required-asterisk">*</span>}
                <HelpIcon
                    text={
                        isNer
                            ? 'List the entity types you expect this task to extract, so the system knows every expected label.'
                            : 'List the labels you expect in your files, so the system knows every expected category.'
                    }
                />
            </div>
            <LabelsTagInput
                labels={labels}
                readOnly={readOnly}
                onChange={(next) => {
                    if (readOnly) return;
                    onPatch({ labels: next });
                }}
            />
            {error && <div className="cw-field-error">{error}</div>}
        </div>
    );
};
